import {LiveChartArrowModel} from '../../models/live-chart-arrow-model';
import {TotalEnergyResponse} from '../../../../api/models/total-energy-response';

export class LiveChartHelper {
  public static calculateArrowProperties(data: TotalEnergyResponse): LiveChartArrowModel {
    if (data.production > 0) {
      return {option: 0, formula: 'Not Relevant'}; // Not relevant, exit early
    }

    let result: LiveChartArrowModel = {};

    // Helper functions for condition checks
    const isBatteryEmpty = data.batteryStatusKwh <= 0;
    const isConsumptionNegative = data.consumption <= 0;
    const isGridNegative = data.grid <= 0;

    const isBatteryCharged = data.batteryStatusKwh > 0;
    const isConsumptionPositive = data.consumption > 0;
    const isGridPositive = data.grid > 0;

    if (isBatteryEmpty && isConsumptionPositive && isGridNegative) {
      const minusProductionMinusConsumption = -data.production - data.consumption;
      if (minusProductionMinusConsumption >= 0) {
        result = this.createModel(
          data.consumption, //1
          0, //2
          minusProductionMinusConsumption, //3
          0, //4
          0, //5
          0, //6
          0, //-4
          -data.batteryStatusKwh, //-5
          0, //-6
          1,
          '1=V, 2=0, 3=-P-V, 4=0, -5=-B, 6=0 (ausgebessert)',
        );
      }
      if (minusProductionMinusConsumption <= 0 && isBatteryEmpty) {
        //formular 2: additional: -P-V < 0
        result = this.createModel(
          -data.production,
          0,
          0,
          0, //4 is negative - therefore use arrow -4!
          0, //5
          0, //6
          data.production + data.consumption, //-4
          -data.grid, //-5
          0, //-6
          2,
          '1=-P, 2=0, 3=0, -4=+V+P, -5=-N, 6=0 (ausgebessert)',
        );
      }
      return result;
    }

    if (isBatteryCharged && isConsumptionPositive && isGridNegative) {
      result = this.createModel(
        data.consumption,
        data.batteryStatusKwh,
        -data.grid,
        0,
        0,
        0, //-4
        0, //-5
        0, //-6
        0,
        3,
        '1=V, 2=B, 3=-N, 4=0, 5=0, 6=0',
      );
      return result;
    }

    if (isBatteryEmpty && isConsumptionNegative && isGridNegative) {
      result = this.createModel(
        0,
        0,
        -data.production, //3
        0, //4
        0, //5
        0, //6
        0, //-4
        -data.batteryStatusKwh, //-5
        -data.consumption, //-6
        4,
        '1=0, 2=0, 3=-P, 4=0, -5=-B, -6=-V (ausgebessert)',
      );
      return result;
    }

    //formula 5 or 6
    const batteryPlusConsumption = data.batteryStatusKwh + data.consumption;
    if (isBatteryCharged && isConsumptionNegative && isGridNegative) {
      if (batteryPlusConsumption >= 0) {
        result = this.createModel(
          0,
          batteryPlusConsumption,
          -data.grid,
          -data.consumption, //4
          0,
          0,
          0, //-4
          0, //-5
          0, //-6
          5,
          '1=0, 2=B+V, 3=-N, 4=-V, 5=0, 6=0',
        );
      } else {
        //Formular 6: Battery is positiv, but B+v is negativ:  1=0, 2=0, 3=-P, 4=B, 5=0, -6 = -V-B
        result = this.createModel(
          0,
          0,
          -data.production, //3
          data.batteryStatusKwh, //4
          0,
          0, //6
          0, //-4
          0, //-5
          -data.consumption - data.batteryStatusKwh, //-6
          6,
          '1=0, 2=0, 3=-P, 4=B, 5=0, -6 = -V-B (ausgebessert)',
        );
      }
      return result;
    }

    if (isBatteryEmpty && isConsumptionPositive && isGridPositive) {
      result = this.createModel(
        -data.production,
        0,
        0,
        0,
        0,
        data.grid,
        -data.batteryStatusKwh, //-4
        0, //-5
        0, //-6
        7,
        '1=-P, 2=0, 3=0, -4=-B, 5=0, 6=N (ausgebessert)',
      );
      return result;
    }

    const consumptionPlusProduction = data.consumption + data.production;
    if (isBatteryCharged && isConsumptionPositive && isGridPositive) {
      if (consumptionPlusProduction >= 0) {
        result = this.createModel(
          -data.production,
          0,
          0,
          0,
          data.batteryStatusKwh,
          consumptionPlusProduction,
          0, //-4
          0, //-5
          0, //-6
          8,
          '1=-P, 2=0, 3=0, 4=0, 5=B, 6=V+P',
        );
      } else {
        result = this.createModel(
          data.consumption,
          -data.production,
          0,
          0,
          data.grid,
          0,
          0, //-4
          0, //-5
          0, //-6
          //debug
          9,
          '1=V, 2=-P, 3=0, 4=0, 5=N, 6=0',
        );
      }
      return result;
    }

    if (isBatteryEmpty && isConsumptionNegative && isGridPositive) {
      return {option: 10, formula: 'Not Relevant'};
    }

    if (isBatteryCharged && isConsumptionNegative && isGridPositive) {
      result = this.createModel(
        0,
        -data.production,
        0,
        0, //4
        data.grid, //5
        0, //6
        -data.consumption, //-4
        0, //-5
        0, //-6
        11,
        '1=0, 2=-P, 3=0, -4=-V, 5=N, 6=0 (ausgebessert)',
      );
      return result;
    }

    return result;
  }

  // Helper function to update arrowModel
  private static createModel(
    prodToCons: number,
    prodToBat: number,
    prodToGrid: number,
    consToBat: number,
    gridToBat: number,
    gridToCons: number,
    batToCons: number,
    batToGrid: number,
    consToGrid: number,
    option: number,
    formula: string,
  ): LiveChartArrowModel {
    return {
      production_to_consumption: prodToCons,
      production_to_battery: prodToBat,
      production_to_grid: prodToGrid,
      consumption_to_battery: consToBat,
      grid_to_battery: gridToBat,
      grid_to_consumption: gridToCons,
      battery_to_consumption: batToCons,
      battery_to_grid: batToGrid,
      consumption_to_grid: consToGrid,
      option: option,
      formula: formula,
    };
  }
}
